import * as React from "react";
import { useMutation, useQuery } from "react-query";
import { ApiError, Branch, SystemSettings, SystemSettingsService, UtilsService, XtrfService } from "gen/clients";
import { PageLoading } from "components/PageLoading/PageLoading";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import Box from "@mui/material/Box";
import { useDialogState } from "hooks/useDialogState";
import { BranchesTable } from "./components/BranchesTable";
import { AddBranchPanel } from "./components/AddBranchPanel";
import { EditBranchPanel } from "./components/EditBranchPanel";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { SnackbarAlert } from "components/SnackbarAlert/SnackbarAlert";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";

const BranchesTab: React.FC = () => {
  const [addPanelOpen, openAddPanel, closeAddPanel] = useDialogState();
  const [editPanelOpen, openEditPanel, closeEditPanel] = useDialogState();
  const [selectedBranch, setSelectedBranch] = React.useState<Branch | null>(null);

  const {
    data: systemSettings,
    isLoading: isLoadingSettings,
    error: settingsError,
    refetch
  } = useQuery<SystemSettings, ApiError>(["getSystemSettings"], {
    queryFn: SystemSettingsService.getSystemSettings
  });

  const lltsBranches = React.useMemo(() => {
    return (systemSettings?.lltsBranches || []).sort((a, b) => a.id?.localeCompare(b.id));
  }, [systemSettings?.lltsBranches]);

  const {
    data: xtrfServices,
    isLoading: isLoadingServices,
    error: servicesError
  } = useQuery<XtrfService[], ApiError>(["listXtrfServices"], {
    queryFn: UtilsService.listXtrfServices
  });

  const {
    mutateAsync: updateSystemSettings,
    isLoading: isUpdating,
    error: updateError,
    isSuccess: updateSuccess
  } = useMutation(SystemSettingsService.updateSystemSettings);

  const handleAddBranch = React.useCallback(
    async (branch: Branch) => {
      if (!systemSettings) {
        return;
      }

      const updatedBranches = [...(systemSettings.lltsBranches || []), branch];

      await updateSystemSettings({
        requestBody: {
          ...systemSettings,
          lltsBranches: updatedBranches
        }
      });

      closeAddPanel();
      refetch();
    },
    [systemSettings, updateSystemSettings, closeAddPanel, refetch]
  );

  const handleEditBranch = React.useCallback(
    async (updatedBranch: Branch) => {
      if (!systemSettings || !selectedBranch) {
        return;
      }

      // Use the original branch ID to find the branch to update (fallback to xtrfId for backward compatibility)
      const updatedBranches = (systemSettings.lltsBranches || []).map(branch => {
        const branchMatches = selectedBranch.id
          ? branch.id === selectedBranch.id
          : branch.xtrfId === selectedBranch.xtrfId;
        return branchMatches ? updatedBranch : branch;
      });

      await updateSystemSettings({
        requestBody: {
          ...systemSettings,
          lltsBranches: updatedBranches
        }
      });

      closeEditPanel();
      refetch();
    },
    [systemSettings, selectedBranch, updateSystemSettings, closeEditPanel, refetch]
  );

  const handleDeleteBranch = React.useCallback(
    async (branchToDelete: Branch) => {
      if (!systemSettings) {
        return;
      }

      const updatedBranches = (systemSettings.lltsBranches || []).filter(branch => {
        // Use ID if available, otherwise fall back to xtrfId for backward compatibility
        return branchToDelete.id ? branch.id !== branchToDelete.id : branch.xtrfId !== branchToDelete.xtrfId;
      });

      await updateSystemSettings({
        requestBody: {
          ...systemSettings,
          lltsBranches: updatedBranches
        }
      });

      await refetch();
      return;
    },
    [systemSettings, updateSystemSettings, refetch]
  );

  const handleEditClick = React.useCallback(
    (branch: Branch) => {
      setSelectedBranch(branch);
      openEditPanel();
    },
    [openEditPanel]
  );

  const isLoading = isLoadingSettings || isLoadingServices;
  const error = settingsError || servicesError;

  if (isLoading) {
    return <PageLoading />;
  }

  if (error) {
    return <ApiErrorMessage apiError={error} />;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Card sx={{ mb: 1 }} elevation={0}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Company Branches
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage LanguageLine company branches. These branches are used for internal organization and reporting.
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openAddPanel}>
          Add Branch
        </Button>
      </Box>

      <BranchesTable
        branches={lltsBranches || []}
        xtrfServices={xtrfServices || []}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteBranch}
        isUpdating={isUpdating}
        updateError={updateError}
      />

      {addPanelOpen && (
        <AddBranchPanel
          onClose={closeAddPanel}
          onSave={handleAddBranch}
          existingBranches={lltsBranches || []}
          xtrfServices={xtrfServices || []}
        />
      )}

      {editPanelOpen && selectedBranch && (
        <EditBranchPanel
          branch={selectedBranch}
          onClose={closeEditPanel}
          onSave={handleEditBranch}
          existingBranches={lltsBranches || []}
          xtrfServices={xtrfServices || []}
        />
      )}

      {updateError && <SnackbarApiError error={updateError} />}
      {updateSuccess && <SnackbarAlert message="Branch settings updated successfully" severity="success" />}
    </Box>
  );
};

export { BranchesTab };
